import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { mockCompanyData } from "@/components/analytics/company-details-sidebar";
import type { GraphQLConnection } from "@/lib/graphql-pagination";
import { format } from "date-fns";

interface CompanyPerformanceGraphQLNode {
  companyId: number;
  organisationId: number;
  companyName: string | null;
  companyCode: string | null;
  currencyCode: string | null;
  totalAR: number;
  totalOverdue: number;
  overduePercent: number;
  avgDso: number;
  cei: number;
  avgDaysToPay: number;
  customerCount: number;
}

interface CompanyPerformanceListGraphQLResponse {
  multiCompanyDashboard: {
    companyPerformanceList: GraphQLConnection<CompanyPerformanceGraphQLNode>;
  };
}

const COMPANY_PERFORMANCE_LIST_QUERY = `
  query CompanyPerformanceList($first: Int, $after: String, $filter: CompanyPerformanceComparisonFilterInput, $sort: [CompanyPerformanceComparisonSortSortByInput!]) {
    multiCompanyDashboard {
      companyPerformanceList(first: $first, after: $after, filter: $filter, sort: $sort) {
        edges {
          node {
            companyId
            companyName
            companyCode
            currencyCode
            totalAR
            totalOverdue
            overduePercent
            avgDso
            cei
            avgDaysToPay
            customerCount
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

export interface CompanyPerformance {
  id: string;
  name: string;
  companyCode: string | null;
  currency: string;
  totalAR: number;
  overdue: number;
  overduePercent: number;
  dso: number;
  cei: number;
  avgDaysToPay: number;
  customerCount: number;
}

function mapNodeToCompanyPerformance(node: CompanyPerformanceGraphQLNode): CompanyPerformance {
  return {
    id: String(node.companyId),
    name: node.companyName ?? "",
    companyCode: node.companyCode,
    currency: node.currencyCode ?? "",
    totalAR: node.totalAR,
    overdue: node.totalOverdue,
    overduePercent: node.overduePercent,
    dso: node.avgDso,
    cei: node.cei,
    avgDaysToPay: node.avgDaysToPay,
    customerCount: node.customerCount,
  };
}

export function useCompanyPerformanceList(currencyFilter?: string) {
  const apiConfig = useApiConfig();

  const demoData: CompanyPerformance[] = mockCompanyData.map(x => ({
    id: x.id,
    name: x.name,
    companyCode: null,
    currency: x.currency,
    totalAR: x.totalAR,
    overdue: x.overdue,
    overduePercent: x.overduePercent,
    dso: x.dso,
    cei: x.cei,
    avgDaysToPay: x.avgDaysToPay,
    customerCount: 0,
  }));

  return useModeAwareQuery<CompanyPerformance[], Error>(
    ["company-performance-graphql-list", currencyFilter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = { first: 500 };

      if (currencyFilter && currencyFilter !== "all") {
        variables.filter = { currencyCode: { eq: currencyFilter } };
      }

      const response = await graphqlQuery<CompanyPerformanceListGraphQLResponse>(
        apiConfig,
        COMPANY_PERFORMANCE_LIST_QUERY,
        variables,
        signal,
      );

      return response.multiCompanyDashboard.companyPerformanceList.edges.map(
        x => mapNodeToCompanyPerformance(x.node),
      );
    },
    demoData,
    {
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
}

// --- Trend data types and hook ---

interface MultiCompanyDailyTrendNode {
  companyId: number;
  snapshotDate: string;
  dso: number;
  cei: number;
}

interface MultiCompanyDailyTrendResponse {
  multiCompanyDashboard: {
    multiCompanyDailyTrendList: GraphQLConnection<MultiCompanyDailyTrendNode>;
  };
}

const DAILY_TREND_LIST_QUERY = `
  query MultiCompanyDailyTrendAll(
    $first: Int,
    $filter: MultiCompanyDailyTrendFilterInput
  ) {
    multiCompanyDashboard {
      multiCompanyDailyTrendList(first: $first, filter: $filter) {
        edges {
          node {
            companyId
            snapshotDate
            dso
            cei
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export interface CompanyTrendMetrics {
  companyId: string;
  dsoChange: number | null;
  ceiChange: number | null;
  earliestSnapshotDate: string | null;
  latestSnapshotDate: string | null;
}

function computeTrendMetrics(
  nodes: MultiCompanyDailyTrendNode[],
): Map<string, CompanyTrendMetrics> {
  const byCompany = new Map<string, MultiCompanyDailyTrendNode[]>();

  for (const node of nodes) {
    const key = String(node.companyId);
    const existing = byCompany.get(key);
    if (existing) {
      existing.push(node);
    } else {
      byCompany.set(key, [node]);
    }
  }

  const result = new Map<string, CompanyTrendMetrics>();

  for (const [companyId, companyNodes] of byCompany) {
    const sorted = [...companyNodes].sort((a, b) =>
      a.snapshotDate.localeCompare(b.snapshotDate),
    );
    const earliest = sorted[0];
    const latest = sorted[sorted.length - 1];

    result.set(companyId, {
      companyId,
      dsoChange: latest.dso - earliest.dso,
      ceiChange: latest.cei - earliest.cei,
      earliestSnapshotDate: earliest.snapshotDate,
      latestSnapshotDate: latest.snapshotDate,
    });
  }

  return result;
}

function buildMockTrendMetrics(periodDays: number): Map<string, CompanyTrendMetrics> {
  const mockTrends: CompanyTrendMetrics[] = [
    { companyId: '1', dsoChange: -3, ceiChange: 5, earliestSnapshotDate: null, latestSnapshotDate: null },
    { companyId: '2', dsoChange: -5, ceiChange: 3, earliestSnapshotDate: null, latestSnapshotDate: null },
    { companyId: '3', dsoChange: 12, ceiChange: -8, earliestSnapshotDate: null, latestSnapshotDate: null },
    { companyId: '4', dsoChange: -2, ceiChange: 2, earliestSnapshotDate: null, latestSnapshotDate: null },
  ];
  // Adjust mock values proportionally for longer periods
  const scale = periodDays / 7;
  const scaled = mockTrends.map(t => ({
    ...t,
    dsoChange: t.dsoChange !== null ? Math.round(t.dsoChange * scale) : null,
    ceiChange: t.ceiChange !== null ? Math.round(t.ceiChange * scale) : null,
  }));
  return new Map(scaled.map(t => [t.companyId, t]));
}

/**
 * Fetches daily trend snapshots for all companies over the given period
 * and returns a map of companyId → computed DSO/CEI trend metrics.
 *
 * @param periodDays - Number of days to look back (e.g. 7, 30, 60, 90)
 */
export function useCompanyTrendData(periodDays: number) {
  const apiConfig = useApiConfig();

  const fromDate = new Date(Date.now() - periodDays * 24 * 60 * 60 * 1000);
  const fromDateStr = format(fromDate, "yyyy-MM-dd");

  const mockData = buildMockTrendMetrics(periodDays);

  return useModeAwareQuery<Map<string, CompanyTrendMetrics>, Error>(
    ["company-trend-data", periodDays],
    async ({ signal }) => {
      const filter = {
        snapshotDate: { ge: fromDateStr },
      };

      const response = await graphqlQuery<MultiCompanyDailyTrendResponse>(
        apiConfig,
        DAILY_TREND_LIST_QUERY,
        { first: 5000, filter },
        signal,
      );

      const nodes = response.multiCompanyDashboard.multiCompanyDailyTrendList.edges.map(
        x => x.node,
      );

      return computeTrendMetrics(nodes);
    },
    mockData,
    {
      staleTime: 0,
      refetchOnMount: "always",
      enabled: periodDays > 0,
    },
  );
}
